import {createFileRoute, notFound, redirect} from "@tanstack/react-router";
import {z as zod} from "zod";
import {PrivateInviteStatus} from "@vesta/vos-types/modules/workspaceLevel/privateInvites/domain/PrivateInvite";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import {UserAdditionalColumnType} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplateAdditionalColumns";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {convertInviteDataToUserTemplateItem, getFixedAndPrefilledAdditionalColumns, type InvitedUser} from "~/domainModels/workspaceModules/inviteModule";
import {submitWorkspacePrivateInviteForm} from "~/utilities/apis";
import {
    getPrivateInviteWithInviteCode,
    getPublicUserDefinedObjectTemplatesWithCaching,
    getUserDefinedObjectsWithCaching,
} from "~/utilities/queries";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {USER__ACCESSIBLE_WORKSPACES__QUERY_KEY} from "~/utilities/queryKeys";
import {isPrivateInviteAllowedForAuthMethods} from "~/domainModels/privateInviteAccess";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {RouteComponent} from "./-routeComponent";

const inviteSearchSchema = zod.object({
    inviteCode: zod.string(),
});

export const Route = createFileRoute("/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/u/additional-details/$inviteId")({
    validateSearch: inviteSearchSchema,
    loader: async ({params, context, location}) => {
        const inviteId = params.inviteId;
        const searchParams = new URLSearchParams(location.search);
        const inviteCodeRaw = searchParams.get("inviteCode");
        const inviteCodeResult = zod.string().safeParse(inviteCodeRaw);
        if (inviteCodeResult.success !== true || inviteCodeResult.data === "") {
            throw notFound();
        }
        const inviteCode = inviteCodeResult.data;

        const inviteResult = await getPrivateInviteWithInviteCode({
            intellsysBaseUrl: context.intellsysBaseUrl,
            accessToken: context.authenticationStatus.accessToken,
            queryClient: context.queryClient,
            inviteCode: inviteCode,
            id: inviteId as Uuid,
        });

        if (inviteResult.success !== true) {
            throw notFound();
        }

        const invite = inviteResult.data;
        if (isPrivateInviteAllowedForAuthMethods({invite, authMethods: context.authenticationStatus.heimdallUser.authMethods}) !== true) {
            throw redirect({to: "/u/select-workspace"});
        }

        if (invite.status === PrivateInviteStatus.pending) {
            throw redirect({to: "/u/select-workspace"});
        }

        const user = convertInviteDataToUserTemplateItem(invite);
        const {fixedDetails: fixedDetailIds} = getFixedAndPrefilledAdditionalColumns(invite);
        const userTemplate = invite.userTemplates.find((template) => template.id === user.templateId);
        const referencedUserDefinedObjectTemplateIds = new Set(
            userTemplate?.additionalColumns.flatMap((column) =>
                column.type === UserAdditionalColumnType.userDefinedObject ||
                column.type === UserAdditionalColumnType.userDefinedObjects
                    ? [column.configuration.userDefinedObjectTemplateId]
                    : [],
            ) ?? [],
        );
        const userDefinedObjectOptionsByTemplateId = new Map<
            Uuid,
            ReadonlyArray<{displayName: string; id: Uuid}>
        >();
        if (referencedUserDefinedObjectTemplateIds.size > 0) {
            const templatesResult = await getPublicUserDefinedObjectTemplatesWithCaching({
                intellsysBaseUrl: context.intellsysBaseUrl,
                queryClient: context.queryClient,
                workspaceId: invite.workspaceId,
            });
            if (templatesResult.success !== true) {
                throw templatesResult.error;
            }
            for (const templateId of referencedUserDefinedObjectTemplateIds) {
                const template = templatesResult.data.find((candidate) => candidate.id === templateId);
                if (template === undefined) {
                    throw new Error(`Referenced User Defined Object template ${templateId} was not found.`);
                }
                const objectsResult = await getUserDefinedObjectsWithCaching({
                    accessToken: context.authenticationStatus.accessToken,
                    intellsysBaseUrl: context.intellsysBaseUrl,
                    queryClient: context.queryClient,
                    templateId: templateId,
                    workspaceId: invite.workspaceId,
                });
                if (objectsResult.success !== true) {
                    throw objectsResult.error;
                }
                userDefinedObjectOptionsByTemplateId.set(
                    templateId,
                    objectsResult.data.map((object) => ({
                        displayName: processOttopilotStringTemplate(
                            template.instanceDisplayNameTemplate,
                            object.additionalColumnValues,
                        ),
                        id: object.id,
                    })),
                );
            }
        }

        if (
            userTemplate !== undefined &&
            (invite.status === PrivateInviteStatus.active ||
                invite.status === PrivateInviteStatus.changesRequested ||
                invite.status === PrivateInviteStatus.rejected) &&
            canSubmitAdditionalDetailsInLoader(userTemplate)
        ) {
            const inviteFormData = buildInviteFormDataForSubmission({user, userTemplate, fixedDetailIds});
            const submitResult = await submitWorkspacePrivateInviteForm({
                privateInviteId: inviteId as Uuid,
                inviteCode,
                inviteFormData,
                intellsysBaseUrl: context.intellsysBaseUrl,
                accessToken: context.authenticationStatus.accessToken,
            });
            if (submitResult.success !== true) {
                throw submitResult.error;
            }
            await context.queryClient.resetQueries({
                queryKey: [USER__ACCESSIBLE_WORKSPACES__QUERY_KEY, "invited", context.authenticationStatus.accessToken],
            });
            await context.queryClient.resetQueries({
                queryKey: [USER__ACCESSIBLE_WORKSPACES__QUERY_KEY, context.authenticationStatus.accessToken],
            });
            throw redirect({to: "/u/select-workspace"});
        }

        return {
            intellsysBaseUrl: context.intellsysBaseUrl,
            inviteId,
            inviteCode,
            invite,
            user,
            fixedDetailIds,
            platformUserDetails: invite.platformUserDetails,
            userTemplates: invite.userTemplates,
            users: invite.users,
            organizationalUnitTemplates: invite.organizationalUnitTemplates,
            organizationalUnits: invite.organizationalUnits,
            userDefinedObjectOptionsByTemplateId,
        };
    },
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});

function canSubmitAdditionalDetailsInLoader(userTemplate: UserTemplate): boolean {
    return (
        userTemplate.additionalColumns.length === 0 ||
        userTemplate.additionalColumns.every((column) => column.type === UserAdditionalColumnType.ous)
    );
}

function buildInviteFormDataForSubmission({
    user,
    userTemplate,
    fixedDetailIds,
}: {
    user: InvitedUser;
    userTemplate: UserTemplate;
    fixedDetailIds: Array<Uuid>;
}): Record<Uuid, unknown> {
    const lockedFixedDetailIdSet = new Set(fixedDetailIds.filter((columnId) => isEmptyValue(user.additionalColumnValues[columnId]) !== true));

    return Object.fromEntries(
        userTemplate.additionalColumns
            .filter((column) => lockedFixedDetailIdSet.has(column.id) !== true)
            .map((column) => [column.id, user.additionalColumnValues[column.id] ?? []]),
    );
}

function isEmptyValue(value: unknown): boolean {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === "string") {
        return value.trim() === "";
    }
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    return false;
}
